// Shared display formatters for both portals — one source of truth for how
// byte sizes and counts render across admin and client UIs.

const SIZE_UNITS = ["B", "KB", "MB", "GB", "TB"];

/** Human-readable byte size, e.g. 2048 -> "2.0 KB"; 0/negative -> "0 B". */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B";
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${SIZE_UNITS[unit]}`;
}

/** Locale-grouped integer, e.g. 1234567 -> "1,234,567". */
export function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/** Pence -> "£59" / "£24.50"; whole pounds drop the decimals. */
export function formatPence(pence: number): string {
  const pounds = pence / 100;
  return `£${pence % 100 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`;
}

/** Monthly price label for a pence amount; 0 renders as "Included". */
export function formatMonthlyPence(pence: number): string {
  if (pence === 0) return "Included";
  return `${formatPence(pence)}/month`;
}

/**
 * D1 CURRENT_TIMESTAMP is "YYYY-MM-DD HH:MM:SS" in UTC — normalize it (ISO
 * strings pass through) and parse to a Date, or null when empty/unparseable.
 */
export function parseTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Locale-rendered timestamp for D1/ISO values. Empty values render as `empty`
 * (default em dash); unparseable values render raw rather than vanishing.
 */
export function formatTimestamp(value: string | null | undefined, empty = "—"): string {
  if (!value) return empty;
  return parseTimestamp(value)?.toLocaleString() ?? value;
}
